import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useSupportTicketTimelineEvents } from "../../hooks/use-support-ticket-data";
import { useTimelineEventFilters } from "../../hooks/use-timeline-event-filters";
import * as styles from "./conversation-timeline.module.css";
import { TimelineEventCard } from "./timeline-event-card";

export interface ConversationTimelineProps {
  supportTicketId: SupportTicketId;
}

export const ConversationTimeline = memo(function ConversationTimeline({
  supportTicketId,
}: ConversationTimelineProps) {
  const { data: events, isLoading } =
    useSupportTicketTimelineEvents(supportTicketId);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterButtonRef, setFilterButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const {
    filterItems,
    selectedFilterItems,
    hasActiveFilters,
    isEventVisible,
    clearFilters,
    setSelectedFilterItems,
  } = useTimelineEventFilters();

  const filteredEvents = useMemo(
    () => events.filter((event) => isEventVisible(event.eventType)),
    [events, isEventVisible],
  );

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  return (
    <Flex className={styles.container} dir="column">
      <RedoMultiselectDropdown
        dropdownAnchor={filterButtonRef}
        dropdownOpen={filterDropdownOpen}
        items={filterItems}
        selectedItems={selectedFilterItems}
        selectionVariant="checkbox"
        setDropdownOpen={setFilterDropdownOpen}
        setSelectedItems={setSelectedFilterItems}
        size="xs"
      />
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        className={styles.toolbar}
        justify="space-between"
      >
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {selectedFilterItems.length} selected
        </Text>
        <Flex align="center" gap="xs">
          {hasActiveFilters && (
            <RedoButton
              hierarchy="tertiary"
              onClick={clearFilters}
              size="xs"
              text="Clear"
            />
          )}
          <RedoButton
            hierarchy="secondary"
            IconLeading={FilterLinesIcon}
            onClick={() => setFilterDropdownOpen((open) => !open)}
            ref={setFilterButtonRef}
            size="xs"
            text="Filter"
          />
        </Flex>
      </Flex>

      {events.length === 0 ? (
        <Flex align="center" className={styles.emptyState} justify="center">
          <Text fontSize="xs" textColor="tertiary">
            No activity yet
          </Text>
        </Flex>
      ) : filteredEvents.length === 0 ? (
        <Flex align="center" className={styles.emptyState} justify="center">
          <Flex align="center" dir="column" gap="xxs">
            <Text fontSize="xs" textColor="primary">
              No activity matches your current filters
            </Text>
            <Text fontSize="xxs" textColor="tertiary">
              Adjust filters to view timeline events
            </Text>
          </Flex>
        </Flex>
      ) : (
        <Flex className={styles.list} dir="column">
          {filteredEvents.map((event) => (
            <TimelineEventCard event={event} key={event.id} />
          ))}
        </Flex>
      )}
    </Flex>
  );
});
